import { useCallback, useMemo, useState } from 'react';
import { DSIcon } from '@eiv2/design-system';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { MetricCard } from '../components/MetricCard';
import { RequestState } from '../components/RequestState';
import { CollapsibleFiltersCard } from '../components/CollapsibleFiltersCard';
import { DateRangePicker } from '../components/DateRangePicker';
import { SourceFilter } from '../components/SourceFilter';
import { CartFlowChart } from '../components/CartFlowChart';
import { FunnelSummary } from '../components/FunnelSummary';
import {
  AbandonmentByStepChart,
  ActivityDurationChart
} from '../components/DashboardInsightCharts';
import { adminApi } from '../api/client';
import { useApiResource } from '../hooks/useApiResource';
import { formatNumber, formatPercent } from '../services/format';
import { abandonmentsByStep, activityDurationHistogram } from '../services/dashboardInsights';
import { getDefaultWindow, type DateRange } from '../services/dateWindow';

/** Amostra máxima por página do BFF (clampLimit max=200). */
const CHECKOUT_SAMPLE_LIMIT = 200;

export function DashboardPage({
  api = adminApi
}: { api?: Pick<typeof adminApi, 'getOverview' | 'getFunnel' | 'listCheckouts'> } = {}) {
  const [window, setWindow] = useState<DateRange>(() => getDefaultWindow());
  const [source, setSource] = useState('');

  const overviewLoader = useCallback(
    (signal: AbortSignal) =>
      api.getOverview({ from: window.from, to: window.to, source: source || undefined }, signal),
    [api, window.from, window.to, source]
  );
  const funnelLoader = useCallback(
    (signal: AbortSignal) =>
      api.getFunnel({ from: window.from, to: window.to, source: source || undefined }, signal),
    [api, window.from, window.to, source]
  );
  const checkoutsLoader = useCallback(
    (signal: AbortSignal) =>
      api.listCheckouts(
        {
          from: window.from,
          to: window.to,
          source: source || undefined,
          limit: CHECKOUT_SAMPLE_LIMIT
        },
        signal
      ),
    [api, window.from, window.to, source]
  );

  const overview = useApiResource(overviewLoader, [api, window.from, window.to, source]);
  const funnel = useApiResource(funnelLoader, [api, window.from, window.to, source]);
  const checkouts = useApiResource(checkoutsLoader, [api, window.from, window.to, source]);

  const chartData = useMemo(() => {
    if (!overview.data) return [];
    return [
      { label: 'Ativos', value: overview.data.activeCheckouts, fill: '#D3C0FF' },
      { label: 'Abandonados', value: overview.data.abandonedCheckouts, fill: '#CC3A3D' },
      { label: 'Aprovados', value: overview.data.convertedCheckouts, fill: '#3DB34D' }
    ];
  }, [overview.data]);

  const checkoutRows = checkouts.data?.data ?? [];
  const abandonmentData = useMemo(() => abandonmentsByStep(checkoutRows), [checkoutRows]);
  const timeData = useMemo(() => activityDurationHistogram(checkoutRows), [checkoutRows]);

  const overviewData = overview.data;
  const funnelData = funnel.data;
  const checkoutsData = checkouts.data;
  const error = overview.error ?? funnel.error ?? checkouts.error;
  const isBootstrapping = overview.isLoading || funnel.isLoading || checkouts.isLoading;

  return (
    <div>
      <PageHeader
        title="Visão geral"
        subtitle="Acompanhamento em tempo real de carrinhos e finalizações, com foco em abandono e conversão."
      />

      <CollapsibleFiltersCard
        className="mb-6"
        storageKey="dashboard"
        title="Filtrar por período e origem"
      >
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start">
          <DateRangePicker className="lg:max-w-[520px]" value={window} onChange={setWindow} />
          <SourceFilter compact value={source} onChange={setSource} />
        </div>
      </CollapsibleFiltersCard>

      {error ? (
        <RequestState
          title="Não foi possível carregar a visão geral"
          error={error}
          onRetry={() => {
            overview.reload();
            funnel.reload();
            checkouts.reload();
          }}
        />
      ) : isBootstrapping || !overviewData || !funnelData || !checkoutsData ? (
        <RequestState
          title="Carregando visão geral"
          message="Buscando métricas, funil e jornadas."
        />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <MetricCard
              icon={<DSIcon name="Activity" size={20} />}
              label="Total de eventos"
              tooltip="Soma de todos os eventos de tracking no período e origem filtrados (abertura, etapas, pagamento etc.). Um volume alto indica tráfego e interação no checkout."
              value={formatNumber(overviewData.totalEvents)}
            />
            <MetricCard
              icon={<DSIcon name="ShoppingCart" size={20} />}
              label="Carrinhos"
              tooltip="Quantidade de jornadas de carrinho no filtro atual. Cada jornada conta uma vez, independentemente de ter sido abandonada ou convertida."
              value={formatNumber(overviewData.totalCheckouts)}
            />
            <MetricCard
              delta={`${overviewData.convertedCheckouts} convertidos`}
              icon={<DSIcon name="Chart" size={20} />}
              label="Taxa de conversão"
              tone="success"
              tooltip="Percentual de carrinhos que chegaram a pagamento aprovado. Compare com o volume absoluto de convertidos: uma taxa alta com poucos carrinhos pode ser enganosa."
              value={formatPercent(overviewData.conversionRate)}
            />
            <MetricCard
              icon={<DSIcon name="TickCircle" size={20} />}
              label="Pagamentos aprovados"
              tone="success"
              tooltip="Número de carrinhos convertidos com pagamento aprovado no período. É o resultado final do funil e o melhor indicador de receita realizada."
              value={formatNumber(overviewData.convertedCheckouts)}
            />
          </div>

          <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
            <SectionCard
              title="Status dos carrinhos"
              subtitle="Ativos, abandonados e convertidos no período"
            >
              <CartFlowChart data={chartData} />
            </SectionCard>

            <SectionCard title="Funil resumido" subtitle="Jornadas distintas por etapa no período">
              <FunnelSummary steps={funnelData.steps} />
            </SectionCard>
          </div>

          <div className="mb-6">
            <SectionCard
              title="Abandono por etapa"
              subtitle={`Barras horizontais · última etapa · amostra de ${formatNumber(checkoutsData.data.length)} jornadas`}
            >
              <AbandonmentByStepChart data={abandonmentData} />
            </SectionCard>
          </div>

          <SectionCard
            title="Duração observada da atividade"
            subtitle={`Da primeira à última atividade, por status atual · amostra de ${formatNumber(checkoutsData.data.length)} jornadas`}
          >
            <ActivityDurationChart data={timeData} />
          </SectionCard>
        </>
      )}
    </div>
  );
}
